import React from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';

/**
 * EmptyState - Enhanced empty state component with CTA and optional illustration
 * 
 * @param {Object} props
 * @param {string} props.title - Main title text
 * @param {string} props.description - Description text
 * @param {React.ReactNode} props.icon - Optional icon or illustration
 * @param {string} props.actionLabel - Label for action button
 * @param {Function} props.onAction - Action button click handler
 * @param {string} props.variant - 'default' | 'minimal' | 'illustrated'
 * @param {Object} props.sx - Additional styling
 */
const EmptyState = ({
  title = 'No data available',
  description,
  icon,
  actionLabel,
  onAction,
  variant = 'default',
  sx = {},
  ...props
}) => {
  const theme = useTheme();

  const getVariantStyles = () => {
    switch (variant) {
      case 'minimal':
        return {
          p: 3,
          textAlign: 'center',
        };
      
      case 'illustrated':
        return {
          p: { xs: 4, md: 6 },
          textAlign: 'center',
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          borderRadius: 3,
        };
      
      default:
        return {
          p: { xs: 4, md: 6 },
          textAlign: 'center',
          minHeight: 300,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        };
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        ...getVariantStyles(),
        bgcolor: variant === 'illustrated' 
          ? alpha(theme.palette.primary.main, 0.02)
          : 'background.paper',
        borderRadius: 3,
        border: variant === 'illustrated' 
          ? `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
          : 'none',
        ...sx,
      }}
      {...props}
    >
      {icon && (
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            justifyContent: 'center',
            color: 'text.secondary',
            '& svg': {
              fontSize: { xs: 64, md: 80 },
              opacity: 0.5,
            },
          }}
        >
          {icon}
        </Box>
      )}
      
      <Typography
        variant="h5"
        component="h3"
        sx={{
          mb: 1.5,
          fontWeight: 600,
          color: 'text.primary',
          fontSize: { xs: '1.25rem', md: '1.5rem' },
        }}
      >
        {title}
      </Typography>
      
      {description && (
        <Typography
          variant="body1"
          sx={{
            mb: actionLabel ? 3 : 0,
            color: 'text.secondary',
            maxWidth: 500,
            mx: 'auto',
            fontSize: { xs: '0.95rem', md: '1rem' },
          }}
        >
          {description}
        </Typography>
      )}
      
      {actionLabel && onAction && (
        <Button
          variant="contained"
          color="primary"
          onClick={onAction}
          sx={{
            mt: 2,
            px: 4,
            py: 1.5,
            borderRadius: 2,
            fontWeight: 600,
            minHeight: 44,
          }}
        >
          {actionLabel}
        </Button>
      )}
    </Paper>
  );
};

export default EmptyState;

